import React, { useState } from 'react';
import { Post } from '../../services/postService';
import { MediaFile } from '../media/types';
import AuthenticatedImage from '../media/AuthenticatedImage';
import { useClient } from '../../contexts/ClientContext';
import { Facebook, Instagram, Twitter, Linkedin, Trash2, Send, Clock, Heart, MessageCircle, Share, Sparkles, User, Loader2, Edit, ChevronLeft, ChevronRight } from 'lucide-react';
import moment from 'moment-timezone';
import { Button } from '../ui/button';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PostEditModal from '../post/PostEditModal';

interface SocialPostPreviewProps {
  post: Post;
  media: MediaFile[];
  onDelete: (postId: string) => void;
  onPublish: (post: Post) => void;
  onSchedule: (post: Post) => void;
  onUnschedule: (post: Post) => void;
  onEdit?: (post: Post) => void; // Add edit callback
  isPublishing?: boolean;
  isDeleting?: boolean;
  currentPublishingPlatform?: string | null;
}

const PlatformIcon = ({ platform, className }) => {
  switch (platform) {
    case 'facebook':
      return <Facebook className={className} />;
    case 'instagram':
      return <Instagram className={className} />;
    case 'twitter':
      return <Twitter className={className} />;
    case 'linkedin':
      return <Linkedin className={className} />;
    default:
      return null;
  }
};

const MediaCarousel: React.FC<{ mediaFiles: MediaFile[], clientId: string }> = ({ mediaFiles, clientId }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  if (mediaFiles.length === 0) return null;

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % mediaFiles.length);
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => prev === 0 ? mediaFiles.length - 1 : prev - 1);
  };

  const currentMedia = mediaFiles[currentIndex];

  return (
    <div className="relative group">
      <div className="overflow-hidden rounded-xl">
        <AuthenticatedImage
          src={currentMedia.thumbnail_url || currentMedia.file_url}
          alt={currentMedia.original_filename}
          className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-300"
          clientId={clientId}
        />
      </div>
      {mediaFiles.length > 1 && (
        <>
          <button
            onClick={prevImage}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
          >
            <ChevronRight size={16} />
          </button>
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/50 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            {currentIndex + 1} / {mediaFiles.length}
          </div>
        </>
      )}
    </div>
  );
};

const SocialPostPreview: React.FC<SocialPostPreviewProps> = ({ post, media, onDelete, onPublish, onSchedule, onUnschedule, onEdit, isPublishing = false, isDeleting = false, currentPublishingPlatform = null }) => {
  const { currentClient, canEdit } = useClient();
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const platform = post.platforms && post.platforms.length > 0 ? post.platforms[0] : 'facebook';

  const handleEdit = () => {
    if (onEdit) {
      onEdit(post);
    } else {
      setIsEditModalOpen(true);
    }
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    if (onSuccess) {
      onSuccess();
    }
  };

  return (
    <motion.div 
      className="relative group"
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
    >
      <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 rounded-3xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      
      <div className={`relative bg-white dark:bg-slate-800/40 backdrop-blur-xl rounded-3xl border border-slate-200/80 dark:border-slate-600/30 overflow-hidden shadow-xl group-hover:shadow-2xl group-hover:shadow-indigo-500/10 transition-all duration-300 ${isPublishing ? 'opacity-75' : ''}`}>
        {/* Publishing Overlay */}
        {isPublishing && (
          <div className="absolute inset-0 bg-indigo-500/10 backdrop-blur-sm z-10 flex items-center justify-center rounded-3xl">
            <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm px-4 py-2 rounded-xl border border-indigo-200/50 dark:border-indigo-600/50 flex items-center space-x-2">
              <Loader2 size={16} className="animate-spin text-indigo-500" />
              <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
                {currentPublishingPlatform ? (
                  <>
                    Publishing to {currentPublishingPlatform.charAt(0).toUpperCase() + currentPublishingPlatform.slice(1)}...
                    {currentPublishingPlatform === 'instagram' && <span className="block text-xs opacity-75">Instagram may take a moment to process media</span>}
                  </>
                ) : (
                  <>
                    Publishing to {post.platforms?.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' & ') || 'Social Media'}...
                    {post.platforms?.includes('instagram') && <span className="block text-xs opacity-75">Instagram may take a moment to process media</span>}
                  </>
                )}
              </span>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200/80 dark:border-slate-600/20">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center border-2 border-slate-200/80 dark:border-slate-600/50">
                <PlatformIcon platform={platform} className="w-2.5 h-2.5 text-indigo-500 dark:text-indigo-400" />
              </div>
            </div>
            
            <div>
              <div className="flex items-center space-x-2">
                <p className="font-semibold text-gray-900 dark:text-white text-base">{currentClient?.name || 'Your Page'}</p>
                {post.status === 'published' && (
                  <span className="px-1.5 py-0.5 text-xs font-medium text-green-700 bg-green-100 dark:text-green-300 dark:bg-green-900/30 rounded-full">
                    Published
                  </span>
                )}
              </div>
              <div className="flex items-center text-xs text-slate-500 dark:text-slate-400">
                <span>{moment(post.created_at).fromNow()}</span>
                <span className="mx-2">·</span>
                <span className="capitalize">{platform}</span>
                {post.status === 'published' && post.published_at && (
                  <>
                    <span className="mx-2">·</span>
                    <span>Published {moment(post.published_at).fromNow()}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {canEdit() && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit();
                }}
                variant="ghost"
                size="sm"
                className="rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100/50 dark:hover:bg-slate-700/50 hover:text-slate-700 dark:hover:text-slate-300 transition-all duration-300"
                title="Edit Post"
              >
                <Edit size={16} />
              </Button>
            )}
            
            <Button
              onClick={(e) => {
                e.stopPropagation();
                console.log('Delete button clicked for post:', post.id);
                console.log('onDelete function:', onDelete);
                onDelete(post.id);
              }}
              variant="ghost"
              size="sm"
              className={`rounded-xl opacity-100 transition-all duration-300 ${
                isDeleting 
                  ? 'bg-red-500 text-white hover:bg-red-600' 
                  : 'text-red-500 dark:text-red-400 hover:bg-red-100/50 dark:hover:bg-red-500/20 hover:text-red-600 dark:hover:text-red-300'
              }`}
              title={isDeleting ? "Click again to confirm delete" : "Delete Post"}
            >
              <Trash2 size={16} />
              {isDeleting && <span className="ml-1 text-xs">Confirm?</span>}
            </Button>
          </div>
        </div>

        {/* Media */}
        {media.length > 0 && (
          <div className="px-4">
            <MediaCarousel mediaFiles={media} clientId={currentClient?.id || ''} />
          </div>
        )}

        {/* Content */}
        <div className="p-4 overflow-hidden">
          <div className="text-slate-600 dark:text-slate-200 text-sm leading-relaxed break-words overflow-wrap-anywhere word-break-break-all whitespace-pre-wrap max-w-full" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {post.content}
          </div>
        </div>



        {/* Actions */}
        <div className="flex items-center justify-end p-3 bg-slate-100/50 dark:bg-slate-700/20 border-t border-slate-200/80 dark:border-slate-600/20">
          <div className="flex items-center space-x-2">
            {post.status === 'scheduled' ? (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onUnschedule(post);
                }}
                variant="outline"
                size="sm"
                className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-yellow-600 dark:text-yellow-400 bg-yellow-100/50 dark:bg-yellow-500/20 border-yellow-300/70 dark:border-yellow-500/50 hover:bg-yellow-200/50 dark:hover:bg-yellow-500/30 hover:text-yellow-700 dark:hover:text-yellow-300 rounded-lg transition-all duration-300"
              >
                <Clock size={12} />
                <span>Unschedule</span>
              </Button>
            ) : (
              <>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSchedule(post);
                  }}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-1 px-2 py-1 text-xs font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700/50 border-slate-300/70 dark:border-slate-600/50 hover:bg-slate-200/50 dark:hover:bg-slate-600/50 hover:text-slate-800 dark:hover:text-white rounded-lg transition-all duration-300"
                >
                  <Clock size={12} />
                  <span>{post.status === 'published' ? 'Re-schedule' : 'Schedule'}</span>
                </Button>
                
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPublish(post);
                  }}
                  disabled={isPublishing}
                  className="flex items-center space-x-1 px-2 py-1 text-xs font-semibold text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 disabled:from-indigo-400 disabled:to-purple-500 disabled:cursor-not-allowed rounded-lg transition-all duration-300 shadow-md hover:shadow-indigo-500/25 disabled:shadow-none"
                >
                  {isPublishing ? (
                    <>
                      <Loader2 size={12} className="animate-spin" />
                      <span>Publishing...</span>
                    </>
                  ) : (
                    <>
                      <Send size={12} />
                      <span>{post.status === 'published' ? 'Re-publish' : 'Publish'}</span>
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Post Edit Modal - only render if no onEdit callback provided */}
      {!onEdit && (
        <PostEditModal
          key={`edit-modal-${post.id}`}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          post={post}
          onSuccess={handleEditSuccess}
        />
      )}
    </motion.div>
  );
};

export default SocialPostPreview;